import { createAction, type UnknownAction } from '@reduxjs/toolkit';

import type { FacetValueBase } from 'Models/index';
import type { ExternalPageSize } from 'Models/pagination';
import type { SearchRequestDefaults } from 'Models/uiConfig/requestTypes';

// Flags that travel in action.meta and are consumed by lastSearchOptions.ts / search.ts epic.
// sendRequest/allowSearchSameAsUri drive dispatch logic; the rest are SearchOptions.
export type RequestMeta = {
  sendRequest?: boolean;
  allowSearchSameAsUri?: boolean;
  checkAutocompleteRedirect?: boolean;
  fromUri?: boolean;
  goToSearchPage?: boolean;
  ignoreLocalPreselection?: boolean;
  mayDiscardValue?: boolean;
  onInit?: boolean;
  stayOnPage?: boolean;
  pathname?: string;
};

// Options that callers may pass alongside data; sendRequest can be overridden (e.g. to false to suppress search).
type SearchOptionsMeta = Omit<RequestMeta, 'allowSearchSameAsUri'>;
type ReplaceRequestPayload = ReplaceRequestData & { protectedFields: string[] };

function createRequestMeta(meta: RequestMeta = {}): RequestMeta {
  return { ...meta, sendRequest: meta.sendRequest ?? true };
}

function createAllowSearchSameAsUriMeta(meta: Omit<RequestMeta, 'allowSearchSameAsUri'> = {}): RequestMeta {
  return { allowSearchSameAsUri: true, ...createRequestMeta(meta) };
}

// ---- no-payload request actions ----

export const loadMore = createAction('LOAD_MORE', () => ({
  payload: undefined,
  meta: createRequestMeta(),
}));

export const setPrevPage = createAction('SET_PREV_PAGE', () => ({
  payload: undefined,
  meta: createRequestMeta(),
}));

export const setNextPage = createAction('SET_NEXT_PAGE', () => ({
  payload: undefined,
  meta: createRequestMeta(),
}));

export const skipRequest = createAction('SKIP_REQUEST');

// ---- typed-data request actions ----

export const setPageNumber = createAction('SET_PAGE_NUMBER', (pageNumber: number) => ({
  payload: { pageNumber },
  meta: createRequestMeta(),
}));

export const setPageSize = createAction('SET_PAGE_SIZE', (pageSize: ExternalPageSize) => ({
  payload: { pageSize },
  meta: createRequestMeta(),
}));

export const setSort = createAction('SET_SORT', (sort: string) => ({
  payload: { sort },
  meta: createRequestMeta(),
}));

export const filterQueryChanged = createAction('FILTER_QUERY_CHANGED', (value: string) => ({
  payload: value,
  meta: createRequestMeta(),
}));

// ---- value + SearchOptions actions ----

export const discard = createAction('DISCARD', (value: FacetValueBase, options: SearchOptionsMeta = {}) => ({
  payload: { value },
  meta: createRequestMeta(options),
}));

export const discardField = createAction(
  'DISCARD_FIELD',
  (field: string, options: SearchOptionsMeta = {}) => ({
    payload: { field },
    meta: createRequestMeta(options),
  }),
);

export const replaceValue = createAction(
  'REPLACE_VALUE',
  (value: FacetValueBase, options: SearchOptionsMeta = {}) => ({
    payload: { value },
    meta: createRequestMeta(options),
  }),
);

export const discardManyFields = createAction(
  'DISCARD_MANY_FIELDS',
  (fields: string[], options: SearchOptionsMeta = {}) => ({
    payload: { fields },
    meta: createRequestMeta(options),
  }),
);

export const replaceManyValues = createAction(
  'REPLACE_MANY_VALUES',
  (values: FacetValueBase[], options: SearchOptionsMeta = {}) => ({
    payload: { values },
    meta: createRequestMeta(options),
  }),
);

export const toggle = createAction(
  'TOGGLE',
  (value: FacetValueBase, options: { isSingleValue?: boolean } & SearchOptionsMeta = {}) => {
    const { isSingleValue, ...searchOptions } = options;
    return {
      payload: { value, isSingleValue },
      meta: createRequestMeta(searchOptions),
    };
  },
);

export const replaceColor = createAction(
  'REPLACE_COLOR',
  (value: FacetValueBase, options: SearchOptionsMeta = {}) => ({
    payload: { value },
    meta: createRequestMeta(options),
  }),
);

export const toggleColor = createAction(
  'TOGGLE_COLOR',
  (value: FacetValueBase, options: SearchOptionsMeta = {}) => ({
    payload: { value },
    meta: createRequestMeta(options),
  }),
);

export const removeMaxCountByField = createAction('REMOVE_MAX_COUNT_BY_FIELD', (field: string) => ({
  payload: { field },
  meta: createAllowSearchSameAsUriMeta(),
}));

export const resetMaxCountByField = createAction('RESET_MAX_COUNT_BY_FIELD', (field: string) => ({
  payload: { field },
  meta: createAllowSearchSameAsUriMeta(),
}));

// ---- query actions ----

export const discardQuery = createAction('DISCARD_QUERY', (options: SearchOptionsMeta = {}) => ({
  payload: undefined,
  meta: createRequestMeta(options),
}));

export const filterQueryResetRequested = createAction(
  'FILTER_QUERY_RESET_REQUESTED',
  (options: SearchOptionsMeta = {}) => ({
    payload: undefined,
    meta: createRequestMeta(options),
  }),
);

// ---- complex data + SearchOptions actions ----

type ReplaceRequestData = Pick<
  SearchRequestDefaults,
  'catalog' | 'filterQuery' | 'pageNumber' | 'pageSize' | 'pageSizeMode' | 'query' | 'selection' | 'sort'
>;

export const replaceRequest = createAction(
  'REPLACE_REQUEST',
  (options: SearchRequestDefaults & SearchOptionsMeta = {}, protectedFields: string[] = []) => {
    const {
      sendRequest,
      checkAutocompleteRedirect,
      fromUri,
      goToSearchPage,
      ignoreLocalPreselection,
      mayDiscardValue,
      onInit,
      stayOnPage,
      pathname,
      catalog,
      filterQuery,
      pageNumber,
      pageSize,
      pageSizeMode,
      query,
      selection,
      sort,
    } = options;
    const payload: ReplaceRequestPayload = {
      protectedFields,
      catalog,
      filterQuery,
      pageNumber,
      pageSize,
      pageSizeMode,
      query,
      selection,
      sort,
    };
    return {
      payload,
      meta: createRequestMeta({
        sendRequest,
        checkAutocompleteRedirect,
        fromUri,
        goToSearchPage,
        ignoreLocalPreselection,
        mayDiscardValue,
        onInit,
        stayOnPage,
        pathname,
      }),
    };
  },
);

export const resetRequest = createAction(
  'RESET_REQUEST',
  (options: SearchOptionsMeta & { discardVehicle?: boolean; allowSearchSameAsUri?: boolean } = {}) => {
    const { discardVehicle, ...metaOptions } = options;
    return {
      payload: { discardVehicle },
      meta: createRequestMeta(metaOptions),
    };
  },
);

export const retryRequest = createAction('RETRY_REQUEST', (requestAction?: Pick<UnknownAction, 'type'>) => ({
  payload: requestAction ? { action: requestAction } : {},
  meta: createAllowSearchSameAsUriMeta(),
}));

// Aggregated action-creator map. Each entry is re-exported individually above;
// this object exists solely to derive `RequestAction` from a single source.
// Adding a new request action requires adding it here so its type joins the union.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const requestActions = {
  discard,
  discardField,
  replaceValue,
  discardManyFields,
  replaceManyValues,
  toggle,
  replaceColor,
  toggleColor,
  loadMore,
  setPageNumber,
  setPageSize,
  setPrevPage,
  setNextPage,
  setSort,
  discardQuery,
  filterQueryResetRequested,
  filterQueryChanged,
  replaceRequest,
  resetRequest,
  retryRequest,
  skipRequest,
  removeMaxCountByField,
  resetMaxCountByField,
} as const;

export type RequestAction = ReturnType<(typeof requestActions)[keyof typeof requestActions]>;
